import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/utils/api";
import {
  ArrowLeft, Upload, Download, CheckCircle2, XCircle, AlertTriangle, Loader, Plus, Pencil, Minus,
} from "lucide-react";

const HEADERS = ["machine_code", "machine_type", "tonnage", "status", "notes"];

const parseCsv = (text) => {
  const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim());
  if (!lines.length) return [];
  const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const idx = HEADERS.map((h) => header.indexOf(h));
  if (idx[0] === -1) throw new Error("CSV must have a 'machine_code' column");
  return lines.slice(1).map((line) => {
    const cols = line.split(",").map((c) => c.trim());
    const row = {};
    HEADERS.forEach((h, i) => {
      const col = idx[i];
      if (col === -1) return;
      const val = cols[col];
      if (val == null || val === "") return;
      if (h === "tonnage") {
        const n = parseInt(val);
        if (!isNaN(n)) row[h] = n;
      } else {
        row[h] = val;
      }
    });
    return row;
  }).filter((r) => r.machine_code);
};

const BulkMachineSetup = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [retireMissing, setRetireMissing] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFile = (e) => {
    setError("");
    setResult(null);
    setPreview(null);
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseCsv(reader.result);
        setRows(parsed);
        if (parsed.length === 0) setError("No valid rows parsed from CSV");
      } catch (err) {
        setError(err.message || "Failed to parse CSV");
      }
    };
    reader.readAsText(f);
  };

  const previewDry = async () => {
    if (rows.length === 0) return;
    setError("");
    try {
      const res = await api.post("/admin/machines/bulk-upsert", {
        rows,
        retire_missing: retireMissing,
        dry_run: true,
      });
      setPreview(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to preview");
    }
  };

  const submit = async () => {
    if (rows.length === 0) return;
    setUploading(true);
    setError("");
    try {
      const res = await api.post("/admin/machines/bulk-upsert", {
        rows,
        retire_missing: retireMissing,
        dry_run: false,
      });
      setResult(res.data);
      setPreview(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to apply");
    } finally {
      setUploading(false);
    }
  };

  const downloadSample = () => {
    const sample = [
      HEADERS.join(","),
      "PRESS-99,Power Press,160,Active,Line B",
      "PRESS-100,Power Press,250,Maintenance,Under PM",
    ].join("\n");
    const blob = new Blob([sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "machines-bulk-sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-5xl mx-auto p-6">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 inline-flex items-center gap-2">
              <Upload size={22} className="text-[#1A3560]" /> Machines Import
            </h1>
            <p className="text-sm text-slate-500">
              Upload a CSV to create or update machines in bulk. New rows are inserted, existing ones are updated field-by-field.
            </p>
          </div>
          <button
            data-testid="machine-sample-btn"
            onClick={downloadSample}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-700 bg-white hover:bg-slate-100 border border-slate-300 px-3 py-2 rounded-lg"
          >
            <Download size={14} /> Sample CSV
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-4 text-xs text-blue-900">
          <p className="font-extrabold mb-1">CSV columns (header row required, order doesn&apos;t matter):</p>
          <code className="font-mono text-blue-700 block">{HEADERS.join(",")}</code>
          <p className="mt-2">
            <b>machine_code</b> is required. <b>tonnage</b> is required only when inserting a new machine. Leave columns blank to keep existing values.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-4">
          <label className="block cursor-pointer">
            <input
              data-testid="machine-csv-input"
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
              className="hidden"
            />
            <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-[#1A3560] transition-colors">
              <Upload size={32} className="mx-auto text-slate-400 mb-2" />
              <p className="font-bold text-slate-700">Click to select a CSV file</p>
              <p className="text-xs text-slate-500 mt-1">or drop it here</p>
            </div>
          </label>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800 mb-4 flex items-center gap-2">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {rows.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-4">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
              <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">
                Preview — {rows.length} row(s) parsed
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <label
                  data-testid="machine-retire-missing-toggle"
                  className="inline-flex items-center gap-2 text-xs font-bold text-slate-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg cursor-pointer select-none"
                  title="Active machines whose machine_code is NOT in this upload will be marked Retired. History is preserved."
                >
                  <input
                    data-testid="machine-retire-missing-checkbox"
                    type="checkbox"
                    checked={retireMissing}
                    onChange={(e) => {
                      setRetireMissing(e.target.checked);
                      setPreview(null);
                    }}
                    className="h-3.5 w-3.5 accent-amber-600"
                  />
                  Retire machines not in this upload
                </label>
                <button
                  data-testid="machine-preview-btn"
                  onClick={previewDry}
                  className="text-xs font-bold text-slate-800 bg-white border border-slate-300 hover:bg-slate-50 px-3 py-1.5 rounded-lg"
                >
                  Dry-run preview
                </button>
                <button
                  data-testid="machine-bulk-submit"
                  onClick={submit}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 text-sm font-extrabold text-white bg-[#1A3560] hover:bg-[#152a4d] disabled:bg-slate-300 px-4 py-2 rounded-lg"
                >
                  {uploading ? <Loader className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
                  {uploading ? "Applying…" : `Apply ${rows.length} row(s)`}
                </button>
              </div>
            </div>

            {preview && (
              <div
                data-testid="machine-preview-banner"
                className="mx-4 my-3 border border-slate-200 bg-slate-50 rounded-xl px-4 py-3 text-xs"
              >
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <PreviewStat label="Will create" value={preview.created} tone="emerald" icon={<Plus size={12} />} />
                  <PreviewStat label="Will update" value={preview.updated} tone="blue" icon={<Pencil size={12} />} />
                  <PreviewStat label="Skipped" value={preview.skipped} tone="slate" icon={<Minus size={12} />} />
                  <PreviewStat
                    label="Will retire"
                    value={(preview.would_retire || []).length}
                    tone="amber"
                    icon={<AlertTriangle size={12} />}
                  />
                </div>
                {retireMissing && (preview.would_retire || []).length > 0 && (
                  <div className="mt-3">
                    <p className="font-extrabold text-amber-800 mb-1">Retire list:</p>
                    <p className="font-mono text-amber-900 break-all">{preview.would_retire.join(", ")}</p>
                  </div>
                )}
                {(preview.blocked_by_open_jobs || []).length > 0 && (
                  <div
                    data-testid="blocked-by-open-jobs"
                    className="mt-3 border border-red-200 bg-red-50 rounded-md px-3 py-2"
                  >
                    <p className="font-extrabold text-red-800 mb-1 inline-flex items-center gap-1.5">
                      <AlertTriangle size={12} /> Blocked by OPEN jobs — Apply will fail with 409:
                    </p>
                    <p className="font-mono text-red-900 text-[11px] break-all">
                      {preview.blocked_by_open_jobs.map((b) => `${b.machine_code} (${b.job_id})`).join(", ")}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 border-b border-slate-200">
                  <tr>
                    {HEADERS.map((h) => (
                      <th key={h} className="px-3 py-2 text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="machine-preview-tbody">
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                      {HEADERS.map((h) => (
                        <td key={h} className="px-3 py-1.5 font-mono">{r[h] ?? "—"}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {result && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200">
              <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">Result</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4">
              <ResultCard label="Created" value={result.created} color="text-emerald-700" bg="bg-emerald-50" icon={<Plus size={16} />} />
              <ResultCard label="Updated" value={result.updated} color="text-blue-700" bg="bg-blue-50" icon={<Pencil size={16} />} />
              <ResultCard label="Skipped" value={result.skipped} color="text-slate-500" bg="bg-slate-50" icon={<Minus size={16} />} />
              <ResultCard label="Retired" value={(result.retired || []).length} color="text-amber-700" bg="bg-amber-50" icon={<AlertTriangle size={16} />} />
            </div>
            {(result.retired || []).length > 0 && (
              <div className="px-4 pb-4 text-xs">
                <p className="font-extrabold text-amber-800 mb-1">Retired codes:</p>
                <p className="font-mono text-amber-900 break-all">{result.retired.join(", ")}</p>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left">Machine</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Details</th>
                  </tr>
                </thead>
                <tbody data-testid="machine-result-tbody">
                  {result.results.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="px-3 py-1.5 font-bold">{r.machine_code}</td>
                      <td className="px-3 py-1.5">
                        <span className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded-full ${
                          r.status === "created" ? "bg-emerald-100 text-emerald-700" :
                          r.status === "updated" ? "bg-blue-100 text-blue-700" :
                          r.status === "skipped" ? "bg-slate-100 text-slate-600" :
                          r.status === "unchanged" ? "bg-slate-100 text-slate-500" :
                          "bg-red-100 text-red-700"
                        }`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-slate-500">{(r.fields || []).join(", ") || r.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const toneClass = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  slate: "bg-slate-50 text-slate-600 border-slate-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
};

const PreviewStat = ({ label, value, tone, icon }) => (
  <div className={`rounded-lg border px-3 py-2 ${toneClass[tone] || toneClass.slate}`}>
    <div className="flex items-center justify-between">
      <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">{label}</p>
      {icon}
    </div>
    <p className="text-xl font-extrabold">{value ?? 0}</p>
  </div>
);

const ResultCard = ({ label, value, color, bg, icon }) => (
  <div className={`${bg} rounded-xl p-3`}>
    <div className="flex items-center justify-between">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
      <div className={color}>{icon}</div>
    </div>
    <p className={`text-2xl font-extrabold ${color}`}>{value}</p>
  </div>
);

export default BulkMachineSetup;
